import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useMemo, useState } from "react"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { mergeMeetings, mergeMeetingsPreflight } from "../../lib/api"
import { notifyError, notifySuccess } from "../../lib/notify"
import type { IMergeableMeeting, IProps } from "./interfaces"
import { MergeMeetingsDialogView } from "./MergeMeetingsDialogView"

const MAX_SOURCES = 20

/**
 * Smart wrapper for MergeMeetingsDialog. Owns selection + query state, runs
 * the preflight check, fires the merge mutation, and surfaces notifications.
 * MergeMeetingsDialogView renders the modal UI.
 *
 * The merge itself requires explicit `discardEdits` opt-in when any of the
 * sources have manual field edits — the view shows the confirm prompt, this
 * wrapper turns it into the second mutation call.
 */
export const MergeMeetingsDialog = ({ opened, onClose, meetings, onMerged }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const company = useCompanyApi()
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [query, setQuery] = useState<string>("")
  const [pendingEdits, setPendingEdits] = useState<string[] | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const reset = (): void => {
    setSelected({})
    setQuery("")
    setPendingEdits(null)
    setSubmitting(false)
  }

  const close = (): void => {
    reset()
    onClose()
  }

  const selectedIds = useMemo(() => Object.keys(selected).filter((id) => selected[id]), [selected])
  const selectedMeetings = useMemo(() => meetings.filter((m) => selected[m.id]), [meetings, selected])
  const templateLock = selectedMeetings[0]?.template_id ?? null

  const pendingEditSubjects = useMemo<string[] | null>(() => {
    if (pendingEdits === null) return null
    return pendingEdits.map((id) => meetings.find((m) => m.id === id)?.subject ?? id)
  }, [pendingEdits, meetings])

  const toggle = (m: IMergeableMeeting): void => {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[m.id]) {
        delete next[m.id]
      } else {
        if (Object.keys(next).length >= MAX_SOURCES) return prev
        next[m.id] = true
      }
      return next
    })
    setPendingEdits(null) // selection changed — preflight result is stale
  }

  const startMerge = async (): Promise<void> => {
    if (!company || selectedIds.length < 2) return
    setSubmitting(true)
    try {
      // Always run preflight first so the user sees the edit-loss warning before
      // confirming. If pendingEdits is non-null we already confirmed and call
      // straight through to the merge with discardEdits=true.
      if (pendingEdits === null) {
        const preflight = await mergeMeetingsPreflight(company, selectedIds)
        if (preflight.hasEdits.length > 0) {
          setPendingEdits(preflight.hasEdits)
          setSubmitting(false)
          return
        }
      }
      const discardEdits = (pendingEdits?.length ?? 0) > 0
      const result = await mergeMeetings(company, selectedIds, discardEdits)
      notifySuccess(i18n._(msg`The merged meeting is being processed.`), i18n._(msg`Merge started`))
      onMerged(result.id)
      close()
    } catch (err) {
      notifyError(err, i18n._(msg`Merge failed`))
      setSubmitting(false)
    }
  }

  return (
    <MergeMeetingsDialogView
      opened={opened}
      onClose={close}
      meetings={meetings}
      query={query}
      onQueryChange={setQuery}
      selectedIds={selectedIds}
      templateLock={templateLock}
      onToggle={toggle}
      pendingEditSubjects={pendingEditSubjects}
      onConfirmDiscardEdits={() => {
        startMerge().catch(() => undefined)
      }}
      onCancelEditConfirm={() => setPendingEdits(null)}
      onMerge={() => {
        startMerge().catch(() => undefined)
      }}
      submitting={submitting}
    />
  )
}
